import React, { useMemo } from 'react';
import styled, { keyframes } from 'styled-components';

/**
 * Christmas ambient effect: snow in depth layers. Sibling of BatFlyby — same fixed,
 * pointer-transparent overlay, same `paused` contract driven by the idle manager in App.tsx.
 *
 * Instead of one element per flake, each layer is a single element whose background is a
 * repeating tile of radial-gradient flakes, painted once and then only composited. The layer
 * slides down by exactly one tile per loop (seamless, because the pattern repeats per tile)
 * while a wrapper sways it sideways. Far layers are small, slow and faint; the near layer is
 * large, fast and bright, which is where the depth comes from. Cost: 2 nodes per layer,
 * transform-only keyframes, no filters, no JS loop. Returns null under prefers-reduced-motion.
 */

interface SnowLayerSpec {
  /** Size of the repeating pattern tile, px. Also the distance one loop travels. */
  tile: number;
  /** Flakes painted into one tile. */
  flakes: number;
  minSize: number;
  maxSize: number;
  opacity: number;
  /** Seconds to travel one tile: tile / fallDuration is the speed in px/s. */
  fallDuration: number;
  /** Horizontal sway amplitude, px, and its half-period in seconds. */
  swayDistance: number;
  swayDuration: number;
}

// Ordered far → near. Performance mode uses the outer two, so depth survives the cut.
const LAYERS: SnowLayerSpec[] = [
  { tile: 260, flakes: 9, minSize: 1.4, maxSize: 2.2, opacity: 0.45, fallDuration: 14, swayDistance: 10, swayDuration: 9 },
  { tile: 320, flakes: 7, minSize: 2.4, maxSize: 3.4, opacity: 0.7, fallDuration: 10, swayDistance: 16, swayDuration: 7 },
  { tile: 400, flakes: 5, minSize: 3.8, maxSize: 5.4, opacity: 0.95, fallDuration: 7, swayDistance: 24, swayDuration: 5.5 },
];

interface SnowLayerProps {
  $tile: number;
  $pattern: string;
  $opacity: number;
  $fallDuration: number;
  $swayDistance: number;
  $paused: boolean;
}

interface SwayProps {
  $swayDistance: number;
  $swayDuration: number;
  $paused: boolean;
}

// One tile down per loop. The layer starts one tile above the viewport, so it is covered
// throughout; the pattern is periodic per tile, so the loop point is invisible.
const fall = keyframes`
  from { transform: translateY(0); }
  to { transform: translateY(var(--snow-tile)); }
`;

const sway = keyframes`
  from { transform: translateX(calc(-1 * var(--snow-sway))); }
  to { transform: translateX(var(--snow-sway)); }
`;

const SnowContainer = styled.div.attrs({
  className: 'snow-rain-container',
})`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  pointer-events: none;
  overflow: hidden;
  z-index: 10000;
`;

const Sway = styled.div<SwayProps>`
  position: absolute;
  inset: 0;
  --snow-sway: ${(props) => props.$swayDistance}px;
  animation: ${sway} ${(props) => props.$swayDuration}s ease-in-out infinite alternate;
  animation-play-state: ${(props) => (props.$paused ? 'paused' : 'running')};
  will-change: transform;
`;

const SnowLayer = styled.div<SnowLayerProps>`
  position: absolute;
  /* Wider than the viewport by the sway amplitude so the edges never show while swaying. */
  left: ${(props) => -props.$swayDistance}px;
  right: ${(props) => -props.$swayDistance}px;
  top: ${(props) => -props.$tile}px;
  bottom: 0;
  background-image: ${(props) => props.$pattern};
  background-size: ${(props) => props.$tile}px ${(props) => props.$tile}px;
  background-repeat: repeat;
  opacity: ${(props) => props.$opacity};
  --snow-tile: ${(props) => props.$tile}px;
  animation: ${fall} ${(props) => props.$fallDuration}s linear infinite;
  animation-play-state: ${(props) => (props.$paused ? 'paused' : 'running')};
  will-change: transform;
`;

// Small deterministic generator: the same seed paints the same tile on every mount, so a
// re-render never reshuffles the sky.
const seeded = (seed: number): (() => number) => {
  let state = seed;
  return () => {
    state = (state * 1664525 + 1013904223) % 4294967296;
    return state / 4294967296;
  };
};

const fmt = (n: number): string => n.toFixed(1);

const buildPattern = (spec: SnowLayerSpec, seed: number): string => {
  const random = seeded(seed);
  const margin = spec.maxSize + 1;
  return Array.from({ length: spec.flakes }, () => {
    const x = margin + random() * (spec.tile - margin * 2);
    const y = margin + random() * (spec.tile - margin * 2);
    const radius = spec.minSize + random() * (spec.maxSize - spec.minSize);
    // A soft 0.8px edge stands in for the blur filter the old flakes carried.
    return `radial-gradient(circle at ${fmt(x)}px ${fmt(y)}px, rgba(255, 255, 255, 1) ${fmt(radius)}px, rgba(255, 255, 255, 0) ${fmt(radius + 0.8)}px)`;
  }).join(', ');
};

const prefersReducedMotion = (): boolean =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

interface SnowRainProps {
  /** Depth layers to render: 3 for the full sky, 2 keeps the far and near layers only. */
  layers?: 2 | 3;
  paused?: boolean;
}

const SnowRain: React.FC<SnowRainProps> = ({ layers = 3, paused = false }) => {
  const reducedMotion = useMemo(prefersReducedMotion, []);

  const sky = useMemo(() => {
    const specs = layers === 2 ? [LAYERS[0], LAYERS[2]] : LAYERS;
    return specs
      .filter((spec): spec is SnowLayerSpec => spec !== undefined)
      .map((spec, i) => ({ spec, pattern: buildPattern(spec, 7 + i * 131) }));
  }, [layers]);

  if (reducedMotion) {
    return null;
  }

  return (
    <SnowContainer>
      {sky.map(({ spec, pattern }) => (
        <Sway key={spec.tile} $swayDistance={spec.swayDistance} $swayDuration={spec.swayDuration} $paused={paused}>
          <SnowLayer
            $tile={spec.tile}
            $pattern={pattern}
            $opacity={spec.opacity}
            $fallDuration={spec.fallDuration}
            $swayDistance={spec.swayDistance}
            $paused={paused}
          />
        </Sway>
      ))}
    </SnowContainer>
  );
};

export default SnowRain;
